import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from 'react-redux';
import type { RootState } from '@/store/store';
import type { Vignette } from '@/types/Vignette';
import { vignettesSelectors } from '@/slices/vignettes/vignettesSlice';
import { updateVignetteScheduling } from '@/services/vignettes';
import {
  attachVariantMarkerToQuestionHtml,
  doesSchedulingQuestionMatchVariant,
  getRenderableAnswerCrossouts,
  stripVariantMarkerFromQuestionHtml,
} from '@/utils/vignetteSchedulingGuards';
import {
  buildPersistableQuestionHighlights,
  normalizeKatexMarkupForPersistence,
} from '@/components/ora/Study/Exam/components/Highlighting/highlightDom';

/** Highlights are written once the student pauses, not on every stroke. */
export const HIGHLIGHT_PERSIST_DEBOUNCE_MS = 600;

type StoredMarks = {
  /** The stored stem, variant marker included, exactly as QBank keeps it. */
  questionHighlights?: string;
  /** Indexed like QBank's: by option, in letter order. */
  answerCrossouts: boolean[];
  /**
   * Whether the stored highlights are known here: read from a source, or
   * edited on this screen. Until then a crossout write leaves them out, so it
   * cannot replace highlights QBank has with a bare stem.
   */
  highlightsKnown: boolean;
};

/** The student's marks for the served variant, as the chat payload carries them (ORA-822). */
export type ServedQuestionMarks = {
  questionHighlights: string | null;
  answerCrossouts: boolean[];
};

/**
 * The marks made in chat this session, so a question that is scrolled away,
 * paged past or reopened in another conversation comes back as it was left.
 */
const sessionMarks = new Map<string, StoredMarks>();

/** Forget the marks made this session. For tests. */
export const clearChatQuestionMarksCache = (): void => sessionMarks.clear();

const HIGHLIGHT_SPAN_RE = /<span\b[^>]*\bclass="[^"]*\bhighlight\b[^"]*"/i;

/**
 * Stored marks as this screen may show them: guarded the way the exam view
 * guards them, so marks written against other stem content never render.
 */
const toRenderableMarks = ({
  questionHighlights,
  answerCrossouts,
  variantId,
  stemHtml,
  optionCount,
}: {
  questionHighlights?: string | null;
  answerCrossouts?: boolean[] | null;
  variantId: string;
  stemHtml: string;
  optionCount: number;
}): StoredMarks => {
  const matches = doesSchedulingQuestionMatchVariant({
    questionHighlights,
    questionHtml: stemHtml,
    variantId,
  });

  return {
    questionHighlights:
      matches && questionHighlights ? questionHighlights : undefined,
    answerCrossouts: getRenderableAnswerCrossouts({
      answerCrossouts,
      answerOptionCount: optionCount,
      questionHighlights,
      questionHtml: stemHtml,
      variantId,
    }),
    // Stored highlights this screen cannot show (written against other stem
    // content) are still stored: a crossout must not replace them.
    highlightsKnown: !questionHighlights || matches,
  };
};

/**
 * The marks QBank has for this exact variant, when the vignette is loaded in
 * the vignettes slice (the student has it in an open block). Only a match
 * counts: the slice may hold another variant of the same vignette.
 */
const resolveMarksFromQBank = ({
  vignette,
  variantId,
  stemHtml,
  optionCount,
}: {
  vignette?: Vignette;
  variantId: string;
  stemHtml: string;
  optionCount: number;
}): StoredMarks | null => {
  if (!vignette || !variantId) return null;

  const variantView = vignette.variantViews?.find(
    (view) => view.variant.id === variantId
  );

  const scheduling = variantView?.scheduling ?? vignette.scheduling;
  const questionHighlights = scheduling?.questionHighlights;

  if (
    !doesSchedulingQuestionMatchVariant({
      questionHighlights,
      questionHtml: stemHtml,
      variantId,
    })
  ) {
    return null;
  }

  return toRenderableMarks({
    questionHighlights,
    answerCrossouts: scheduling?.answerCrossouts,
    variantId,
    stemHtml,
    optionCount,
  });
};

// A stem image that had not loaded when the stem was read carries the
// loader's `opacity: 0`; stored as-is it would stay invisible wherever the
// stem is shown next.
const stripUnloadedImageOpacity = (html: string): string => {
  if (!/opacity:\s*0(?![.\d])/i.test(html)) return html;

  const container = document.createElement('div');
  container.innerHTML = html;

  container.querySelectorAll<HTMLElement>('[style]').forEach((element) => {
    if (element.style.opacity !== '0') return;

    element.style.removeProperty('opacity');

    if (!element.getAttribute('style')?.trim()) {
      element.removeAttribute('style');
    }
  });

  return container.innerHTML;
};

const fitCrossouts = (values: boolean[], optionCount: number) =>
  Array.from({ length: optionCount }, (_, index) => values[index] === true);

type UseChatQuestionMarksParams = {
  vignetteId: string;
  /** Empty when the payload has none; the marks then stay on this screen. */
  variantId: string;
  stemHtml: string;
  /** The option letters as the block resolved them. */
  optionLetters: string[];
  /** The marks the payload carries; absent on history served before them. */
  served?: ServedQuestionMarks;
  userId?: string;
};

export type ChatQuestionMarks = {
  /** Identifies the question the marks belong to. */
  marksKey: string;
  /** The stem to render: the stored highlighted stem, else the served one. */
  stemHtml: string;
  isCrossedOut: (letter: string) => boolean;
  toggleCrossout: (letter: string) => void;
  /** Takes the stem container's markup after a highlight change. */
  onHighlightsChange: (renderedStemHtml: string) => void;
};

/**
 * Stem highlights and answer crossouts for a question served in chat
 * (ORA-820), stored where QBank stores them — the student's metadata for that
 * variant — so marks made here show in QBank and the other way round.
 *
 * State is local and optimistic. A failed write is logged and the marks stay;
 * a stray error toast over a highlight would be worse than a lost one.
 */
export const useChatQuestionMarks = ({
  vignetteId,
  variantId,
  stemHtml,
  optionLetters,
  served,
  userId,
}: UseChatQuestionMarksParams): ChatQuestionMarks => {
  const store = useStore<RootState>();
  const marksKey = `${vignetteId}:${variantId}`;
  // Per student too: the cache outlives a sign-out in the same tab.
  const cacheKey = `${userId ?? ''}:${marksKey}`;
  const optionCount = optionLetters.length;
  const canPersist = Boolean(vignetteId && variantId);

  // Read once per question, not subscribed: the rendered stem is changed in
  // place by the highlighter, so it must not be re-rendered from new data.
  const hydrated = useMemo<StoredMarks>(() => {
    // Newest first: marks made here this session, then the payload's copy of
    // the stored marks, then QBank's when the vignette is open in a block.
    const cached = sessionMarks.get(cacheKey);
    if (cached) return cached;

    if (served && variantId) {
      return toRenderableMarks({
        ...served,
        variantId,
        stemHtml,
        optionCount,
      });
    }

    const fromQBank = resolveMarksFromQBank({
      vignette: vignettesSelectors.selectById(store.getState(), vignetteId),
      variantId,
      stemHtml,
      optionCount,
    });

    // Nothing to go on (history served before the payload carried marks):
    // start empty, and write only what is changed here.
    return fromQBank ?? { answerCrossouts: [], highlightsKnown: false };
  }, [marksKey]);

  const initialStemHtml = useMemo(
    () =>
      hydrated.questionHighlights &&
      HIGHLIGHT_SPAN_RE.test(hydrated.questionHighlights)
        ? normalizeKatexMarkupForPersistence(
            stripVariantMarkerFromQuestionHtml(hydrated.questionHighlights)
          )
        : stemHtml,
    [hydrated]
  );

  const [crossoutState, setCrossoutState] = useState({
    key: marksKey,
    values: fitCrossouts(hydrated.answerCrossouts, optionCount),
  });

  const crossouts =
    crossoutState.key === marksKey
      ? crossoutState.values
      : fitCrossouts(hydrated.answerCrossouts, optionCount);

  // The latest marks, readable from the timers and handlers without waiting
  // for a render.
  const latest = useRef<StoredMarks & { key: string }>({
    key: marksKey,
    ...hydrated,
  });

  if (latest.current.key !== marksKey) {
    latest.current = { key: marksKey, ...hydrated };
  }

  const pendingHighlightWrite = useRef<{
    timer: number;
    questionHighlights: string;
  } | null>(null);

  const cancelPendingHighlightWrite = () => {
    if (pendingHighlightWrite.current === null) return;

    window.clearTimeout(pendingHighlightWrite.current.timer);
    pendingHighlightWrite.current = null;
  };

  const persist = useCallback(
    async (data: {
      questionHighlights?: string;
      answerCrossouts?: boolean[];
    }) => {
      if (!canPersist) return;

      try {
        await updateVignetteScheduling(vignetteId, data, {
          vignetteVariantId: variantId,
          syncStore: false,
        });
      } catch (error) {
        console.error('Unable to persist chat question marks', error);
      }
    },
    [canPersist, variantId, vignetteId]
  );

  const remember = (marks: StoredMarks) => {
    latest.current = { key: marksKey, ...marks };
    sessionMarks.set(cacheKey, marks);
  };

  // A highlight still waiting on its debounce is written straight away when
  // the question goes away, rather than dropped.
  useEffect(
    () => () => {
      const pending = pendingHighlightWrite.current;
      if (pending === null) return;

      window.clearTimeout(pending.timer);
      pendingHighlightWrite.current = null;

      void persist({ questionHighlights: pending.questionHighlights });
    },
    [marksKey, persist]
  );

  // A highlight write carries the highlights only, never the crossouts.
  const onHighlightsChange = (renderedStemHtml: string) => {
    const stem = stripUnloadedImageOpacity(renderedStemHtml);

    const questionHighlights = variantId
      ? buildPersistableQuestionHighlights({ html: stem, variantId })
      : stem;

    remember({
      questionHighlights,
      answerCrossouts: latest.current.answerCrossouts,
      highlightsKnown: true,
    });

    cancelPendingHighlightWrite();

    pendingHighlightWrite.current = {
      questionHighlights,
      timer: window.setTimeout(() => {
        pendingHighlightWrite.current = null;
        void persist({ questionHighlights });
      }, HIGHLIGHT_PERSIST_DEBOUNCE_MS),
    };
  };

  // QBank indexes crossouts by option in letter order; do the same so a mark
  // made here lands on the same option there.
  const sortedLetters = [...optionLetters].sort((left, right) =>
    left.localeCompare(right)
  );

  const isCrossedOut = (letter: string) =>
    crossouts[sortedLetters.indexOf(letter)] === true;

  const toggleCrossout = (letter: string) => {
    const index = sortedLetters.indexOf(letter);
    if (index === -1) return;

    const answerCrossouts = fitCrossouts(
      latest.current.answerCrossouts,
      optionCount
    );

    answerCrossouts[index] = !answerCrossouts[index];

    const { highlightsKnown } = latest.current;

    // QBank only shows crossouts next to a stored stem for the same variant,
    // so the stem goes with them, as the exam view sends it — but only when
    // the stored highlights are known here. Otherwise the write carries the
    // crossouts alone and leaves QBank's highlights as they are.
    const questionHighlights = highlightsKnown
      ? (latest.current.questionHighlights ??
        attachVariantMarkerToQuestionHtml(stemHtml, variantId))
      : undefined;

    remember({ questionHighlights, answerCrossouts, highlightsKnown });
    setCrossoutState({ key: marksKey, values: answerCrossouts });

    if (questionHighlights === undefined) {
      void persist({ answerCrossouts });
      return;
    }

    // This write carries the latest highlights too.
    cancelPendingHighlightWrite();

    void persist({ answerCrossouts, questionHighlights });
  };

  return {
    marksKey,
    stemHtml: initialStemHtml,
    isCrossedOut,
    toggleCrossout,
    onHighlightsChange,
  };
};
